/**
 * Позиции корзины, которые в выбранном филиале заказать нельзя.
 *
 * Одно правило на всех потребителей: карточка в списке рисует такую позицию красной,
 * а подвал корзины блокирует кнопку оформления. Раньше блокировки не было вовсе —
 * набор, которого в филиале нет, оставался кликабельным, и заказ уходил на сервер
 * (а до фикса доступности комбо — и на кассу, с артикулами чужой точки).
 */

interface MenuLikeProduct {
  id?: string | number
  stop_list?: boolean
  variants?: Array<{ id?: string | number, stop_list?: boolean }>
}

interface CartLikeItem {
  itemType?: string
  id?: string | number
  productId?: string | number
  comboId?: string | number
  name?: string
  stop_list?: boolean
  combo_slot_unavailable?: boolean
}

/**
 * Ищет позицию в меню филиала — с учётом того, что карточка может быть вариантом группы.
 * Возвращает `undefined`, если товара в меню нет вовсе.
 */
function findInMenu(products: MenuLikeProduct[], productId: string): MenuLikeProduct | undefined {
  for (const product of products) {
    if (String(product.id) === productId) return product

    const variant = product.variants?.find(v => String(v.id) === productId)
    if (variant) return variant as MenuLikeProduct
  }

  return undefined
}

export function isCartItemUnavailable(
  item: CartLikeItem,
  allProducts: MenuLikeProduct[],
): boolean {
  if (!item) return false

  // Набор: признаки считает сервер (авторизованный) или guestBranchMigration (гость).
  if (item.itemType === 'combo' || item.comboId) {
    return item.stop_list === true || item.combo_slot_unavailable === true
  }

  const productId = String(item.productId ?? item.id ?? '')
  if (!productId) return false

  const menuItem = findInMenu(allProducts, productId)

  // Товара нет в меню филиала: снят с продажи, удалён или остался после смены филиала.
  if (!menuItem) return true

  return menuItem.stop_list === true
}

/**
 * Недоступные позиции корзины.
 *
 * ВАЖНО: пустой каталог (меню ещё грузится или запрос упал) — не повод считать корзину
 * непригодной. Иначе кнопка оформления гасла бы на каждой холодной загрузке страницы,
 * пока не приедет меню. Комбо при этом проверяются всегда: их признаки приходят
 * вместе с корзиной и от каталога не зависят.
 */
export function findUnavailableItems<T extends CartLikeItem>(
  items: T[],
  allProducts: MenuLikeProduct[],
): T[] {
  if (!Array.isArray(items) || items.length === 0) return []

  const menuLoaded = Array.isArray(allProducts) && allProducts.length > 0

  return items.filter((item) => {
    const isCombo = item?.itemType === 'combo' || Boolean(item?.comboId)
    if (!isCombo && !menuLoaded) return false

    return isCartItemUnavailable(item, allProducts)
  })
}
